﻿import { DefaultLocale, LocaleInfo, getSupportedLocale, CultureInfo } from "./LocalizationSupport";
import { LabelResolver, MessageFile } from "./LabelResolver";

/** Manages the loading and resolution of labels. */
export abstract class LabelLoader {

    /** The object responsible for resolving labels. */
    private resolver: LabelResolver;

    /**
     * @param resolver The object responsible for resolving labels.
     */
    constructor(resolver: LabelResolver) {
        this.resolver = resolver;
    }

    /** The application's current default locale. */
    public get CurrentLocale(): string {
        return this.resolver.CurrentLocale;
    }

    public set CurrentLocale(value: string) {
        this.resolver.CurrentLocale = value;
    }

    /**
     * Prepares this LabelLoader for use by loading the labels for the default locale.
     * @returns A Promise that is resolved once the default labels are loaded.
     */
    public initialize(): Promise<{}> {
        return this.loadLabels(DefaultLocale);
    }

    /**
     * Loads labels for the provided locale.
     * @param locale The locale to load labels for.
     * @returns A Promise that is resolved with the locale used once the labels are loaded.
     */
    public loadLabels(locale: string): Promise<string> {
        let labelsLoadedPromise: Promise<string> = null;
        locale = getSupportedLocale(locale);

        // Do not reload the label file if it is already loaded
        if (this.resolver.getLoadedLocales().indexOf(locale) === -1) {
            let localePathSegment = "";

            localePathSegment = (<CultureInfo>LocaleInfo[locale.toUpperCase()]).labelFile

            labelsLoadedPromise = this.loadResourceFiles(localePathSegment)
                .then((messageFiles) => {
                    for (const messages of messageFiles) {
                        let actualMessageFile = messages;
                        // Use the "default" export if it exists
                        if (messages.default) {
                            // This is a explicit cast through unknown as the typing doesn't accurately represent the import
                            actualMessageFile = messages.default as unknown as MessageFile;
                        }
                        this.resolver.addLabels(locale, actualMessageFile);
                    }

                    return locale;
                });
        } else {
            labelsLoadedPromise = Promise.resolve(locale);
        }

        return labelsLoadedPromise;
    }

    /**
     * Gets the resource file contents.
     * @returns The array of resource files.
     */
    protected abstract loadResourceFiles(pathSegment: string): Promise<MessageFile[]>;
}
